/**
 * @module ui/pages/runs/test-runs/TestRunsFilterBar
 *
 * The test-runs toolbar: Target / Status / Run by as fixed-size menus, the
 * "More filters" dialog trigger (date range — see `TestRunsFilters.tsx`), a
 * Clear action, and the active filter chips row. Built from the shared
 * `@chatsdet/ui` `FilterToolbar` primitives — mirrors `IssuesFilterBar.tsx`.
 * No free-text search: the runs list/trend endpoints have no search param.
 */
import React from 'react';
import {
  Button,
  FilterToolbar,
  FilterToolbarChips,
  FilterToolbarDivider,
  ToolbarFilterMenu,
  type FilterToolbarChip,
  type MultiSelectOption,
} from '@chatsdet/ui';
import { STATUS_LABELS } from './TestRunsFilters';
import type { TestRunsSearch } from '../runsRouteSearch';

export const RUN_STATUS_OPTIONS: MultiSelectOption[] = Object.entries(STATUS_LABELS).map(
  ([id, label]) => ({ id, label })
);

export type TestRunsFilterBarProps = {
  targetFilter: string[];
  targetOptions: MultiSelectOption[];
  statusFilter: string[];
  triggeredByFilter: string[];
  triggeredByOptions: MultiSelectOption[];

  onFilterChange: (updates: Partial<TestRunsSearch>) => void;

  filterDialogTrigger: React.ReactNode;
  activeFilterChips: FilterToolbarChip[];
  onChipRemove: (chipId: string) => void;
  onClearAll: () => void;
  showClearAll: boolean;
  /** Fuse with the sparkline strip + table below: square bottom corners, no bottom border. */
  attached?: boolean;
};

export function TestRunsFilterBar({
  targetFilter,
  targetOptions,
  statusFilter,
  triggeredByFilter,
  triggeredByOptions,
  onFilterChange,
  filterDialogTrigger,
  activeFilterChips,
  onChipRemove,
  onClearAll,
  showClearAll,
  attached = false,
}: TestRunsFilterBarProps) {
  const handleTargetChange = React.useCallback(
    (ids: string[]) => {
      onFilterChange({ targets: ids.length > 0 ? ids : undefined, page: 1 });
    },
    [onFilterChange]
  );

  const handleStatusChange = React.useCallback(
    (ids: string[]) => {
      onFilterChange({ statuses: ids.length > 0 ? ids : undefined, page: 1 });
    },
    [onFilterChange]
  );

  const handleTriggeredByChange = React.useCallback(
    (ids: string[]) => {
      onFilterChange({ triggeredBy: ids.length > 0 ? ids : undefined, page: 1 });
    },
    [onFilterChange]
  );

  return (
    <FilterToolbar attached={attached}>
      <ToolbarFilterMenu
        label="Target"
        options={targetOptions}
        selectedIds={targetFilter}
        onChange={handleTargetChange}
      />
      <ToolbarFilterMenu
        label="Status"
        options={RUN_STATUS_OPTIONS}
        selectedIds={statusFilter}
        onChange={handleStatusChange}
        searchable={false}
      />
      <ToolbarFilterMenu
        label="Run by"
        options={triggeredByOptions}
        selectedIds={triggeredByFilter}
        onChange={handleTriggeredByChange}
      />

      <FilterToolbarDivider />

      {filterDialogTrigger}

      {showClearAll && (
        <Button variant="ghost" size="sm" className="text-muted-foreground" onPress={onClearAll}>
          Clear
        </Button>
      )}

      <FilterToolbarChips chips={activeFilterChips} onRemove={onChipRemove} />
    </FilterToolbar>
  );
}
